import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { LINKS, ASSETS } from "../lib/constants";

const SectionLabel = ({ children, dark = false, onYellow = false }) => (
  <span className={`section-label ${dark ? "on-dark" : ""} ${onYellow ? "on-yellow" : ""}`}>{children}</span>
);

const MapsTo = ({ children }) => (
  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-brand-black/70 font-bold">Maps to: {children}</p>
);

export default function WorkWithMe() {
  return (
    <div data-testid="work-with-me-page">
      {/* HERO */}
      <section className="bg-white pt-12 pb-16 lg:pt-16 lg:pb-20" data-testid="work-hero-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-center reveal">
            <div className="lg:col-span-7">
              <SectionLabel>Work With Me</SectionLabel>
              <h1 className="font-display text-[clamp(2.4rem,6vw,5.2rem)] mt-6 leading-[0.92]">
                Your place in the <span className="hi-yellow">Follow The Cash™</span> journey.
              </h1>

              <h2 className="font-display text-3xl sm:text-4xl mt-12 leading-[1]">
                Start where it hurts most. You don't need to jump into the deepest end first.
              </h2>
              <div className="mt-6 space-y-5 text-lg leading-relaxed text-brand-ink max-w-3xl">
                <p>
                  You might need a quick reality check to see where the problem lives. You might need
                  a simple tool to help you look ahead. You might need a human pair of eyes on the
                  bigger picture. Or you might be ready for ongoing support as the business grows.
                </p>
                <p>
                  Every offer maps to a part of the <strong>Follow The Cash™</strong> method — so you
                  always know where you are in the journey.
                </p>
              </div>

              <div className="mt-9 flex flex-wrap gap-3">
                <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" className="btn-yellow" data-testid="work-hero-reality-btn">
                  Get your free Reality Check <ArrowUpRight size={18} />
                </a>
                <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="work-hero-book-btn">
                  Book a free 20-min chat
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] max-w-[460px] mx-auto lg:ml-auto">
                <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-yellow rounded-[10px]" />
                <div className="absolute inset-0 -translate-x-1 -translate-y-1 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                  <img src={ASSETS.workWithMeHero} alt="Magdalena working with a client outdoors" className="w-full h-full object-cover" data-testid="work-hero-portrait" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 01 REALITY CHECK */}
      <section className="bg-brand-yellow py-16 lg:py-20 border-y-[1.5px] border-brand-black" data-testid="service-reality-check-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-baseline gap-4">
            <span className="font-display text-7xl text-brand-black leading-none">01</span>
            <span className="text-xs uppercase tracking-[0.18em] text-brand-black/80 font-bold">Free · 3 minutes</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-[0.95]">The Profitable-But-Broke Reality Check</h2>
          <MapsTo>Part 1 — Stop Hiding</MapsTo>
          <p className="mt-5 text-lg leading-relaxed text-brand-black max-w-3xl">
            A free 3-minute reality check that tells you exactly where you sit on the
            Profitable-But-Broke spectrum — and which part of Follow The Cash™ you need most right
            now.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-brand-black">
            {[
              "See your score across all four parts of the method",
              "Get a personalised diagnosis of where your biggest money gap lives",
              "Receive a recommended next step — no sales call required",
            ].map((x) => <li key={x} className="flex gap-2.5"><Check size={18} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
          </ul>
          <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" className="btn-primary shadow-w-default mt-8" data-testid="service-reality-check-cta">
            Get your free Reality Check <ArrowUpRight size={18} />
          </a>
        </div>
      </section>

      {/* 02 FORECASTLY */}
      <section className="bg-white py-16 lg:py-20" data-testid="service-forecastly-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-baseline gap-4">
              <span className="font-display text-7xl text-brand-yellow leading-none">02</span>
              <span className="text-xs uppercase tracking-[0.18em] text-brand-stone font-bold">Software</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-[0.95] inline-block">
              <span className="bg-brand-yellow px-2">forecastly</span>
            </h2>
            <div className="mt-5">
              <MapsTo>Part 3 — Take Control</MapsTo>
            </div>
            <p className="mt-5 text-lg leading-relaxed text-brand-ink max-w-2xl">
              For small business owners who want cashflow clarity without the spreadsheet panic.
              forecastly is my guided forecasting tool, built by ProfitMIND around the Follow The
              Cash™ method — to help you see your next 12 months in plain English.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-brand-ink">
              {[
                "Build a simple, rolling 12-month cashflow forecast",
                "Track actuals and start a calmer monthly money rhythm",
                "Use plain-English AI-powered insights to understand what's going on",
                "Set goals and track progress to achieve your business dreams",
                "Live community calls — you're not figuring this out on your own",
                "Perfect if you want clarity without private 1:1 mentoring yet",
              ].map((x) => <li key={x} className="flex gap-2.5"><Check size={18} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
            </ul>
            <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" className="btn-yellow mt-8" data-testid="service-forecastly-cta">
              Visit forecastly <ArrowUpRight size={18} />
            </a>
          </div>
          <div className="lg:col-span-5">
            <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" data-testid="services-forecastly-logo-link" className="block bg-white border-[1.5px] border-brand-black rounded-[10px] p-7 flex items-center justify-center min-h-[260px] transition-shadow duration-300 hover:shadow-[0_10px_24px_rgba(10,10,10,0.18)]" aria-label="Visit forecastly">
              <img src={ASSETS.forecastlyLogo} alt="forecastly by ProfitMIND" className="max-w-full max-h-[220px] object-contain" data-testid="services-forecastly-logo" />
            </a>
          </div>
        </div>
      </section>

      {/* 03 MONEY FLOW SESSION */}
      <section className="bg-brand-yellow py-16 lg:py-20 border-y-[1.5px] border-brand-black" data-testid="service-moneyflow-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-baseline gap-4">
            <span className="font-display text-7xl text-brand-black leading-none">03</span>
            <span className="text-xs uppercase tracking-[0.18em] text-brand-black/80 font-bold">Strategy Session · 90 mins</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-[0.95]">Money Flow Strategy Session</h2>
          <MapsTo>Part 2 — Start Knowing</MapsTo>
          <p className="mt-5 text-lg leading-relaxed text-brand-black max-w-3xl">
            A focused 90-minute session for the business owner who wants to understand what's
            working, what's leaking and what decision needs to come next.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-brand-black">
            {[
              "90-minute 1:1 online video session",
              "Simple intake snapshot before we meet",
              "Revenue, offers, expenses, personal pay and decision clarity",
              "30/60/90-day action plan",
              "Complimentary 30-minute check-in after 60 days",
              "Discount code for forecastly software — your 'Take Control' tool",
            ].map((x) => <li key={x} className="flex gap-2.5"><Check size={18} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
          </ul>
          <Link to={LINKS.moneyFlowSession} className="btn-primary shadow-w-default mt-8" data-testid="service-moneyflow-cta">
            View session details &amp; book <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      {/* 04 ADVISORY */}
      <section className="bg-brand-black text-white py-20 lg:py-24" data-testid="service-advisory-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-baseline gap-4">
            <span className="font-display text-7xl text-brand-yellow leading-none">04</span>
            <span className="text-xs uppercase tracking-[0.18em] text-brand-yellow font-bold">Advisory · 3–12 months</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-[0.95] text-white">
            Profit &amp; Cashflow Advisory Programme
          </h2>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-brand-yellow font-bold">Maps to: Part 4 — Own Your Future</p>
          <p className="mt-5 text-[17px] leading-relaxed text-white max-w-3xl">
            Private ongoing support for business owners who want someone calm, practical and
            commercially-minded beside them — a strategic second brain — while they make bigger
            profit, cashflow and growth decisions.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-white">
            {[
              "3, 6 or 12-month partnership",
              "Regular strategy calls and decision support",
              "Profit, pay, cashflow and offer clarity",
              "Access to forecastly software included for the duration of the partnership",
              "Best for established businesses with real momentum and heavier decisions",
            ].map((x) => <li key={x} className="flex gap-2.5"><Check size={18} className="text-brand-yellow shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
          </ul>
          <Link to={LINKS.financialLeadership} className="btn-yellow btn-yellow-on-dark mt-8" data-testid="service-advisory-cta">
            Explore the Programme <ArrowUpRight size={18} />
          </Link>

          <div className="mt-10 rounded-[10px] border-[1.5px] border-brand-yellow p-7 bg-white/[0.03] max-w-3xl">
            <p className="text-xs uppercase tracking-[0.18em] text-brand-yellow font-bold mb-3">Bonus</p>
            <p className="text-[15px] leading-relaxed text-white">
              Every Advisory Programme client receives <strong className="text-brand-yellow">free access</strong>{" "}
              to forecastly — my cashflow forecasting software — for the duration of the partnership.
              Your 'Take Control' tool, included.
            </p>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-white py-20 lg:py-24" data-testid="services-comparison-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center reveal">
            <SectionLabel>Money Flow Session vs Advisory Programme</SectionLabel>
            <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
              Which kind of support<br />
              <span className="hi-yellow">fits right now?</span>
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 rounded-[10px] border-[1.5px] border-brand-black overflow-hidden" data-testid="services-comparison-table">
            <div className="bg-white p-8 sm:p-10 flex flex-col md:border-r-[1.5px] border-brand-black/15">
              <p className="text-xs uppercase tracking-[0.18em] text-brand-stone font-bold">One focused session</p>
              <h3 className="font-display text-3xl sm:text-4xl mt-4 uppercase leading-tight">Money Flow Strategy Session</h3>
              <p className="mt-5 text-[15px] leading-relaxed text-brand-ink">
                Best when you need one focused session, a clearer picture and a practical next
                step. Ideal if you have one pressing decision, want a diagnostic look at the
                numbers, or aren't ready for ongoing support yet.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-brand-ink flex-grow">
                Think of this as your <strong>Part 2 'Start Knowing'</strong> deep dive — learn
                what your numbers actually say.
              </p>
              <Link to={LINKS.moneyFlowSession} className="btn-secondary shadow-w-hover mt-9 self-start" data-testid="comparison-session-cta">
                Book the Money Flow Session <ArrowUpRight size={16} />
              </Link>
            </div>

            <div className="bg-brand-yellow p-8 sm:p-10 flex flex-col">
              <p className="text-xs uppercase tracking-[0.18em] text-brand-black/80 font-bold">Ongoing partnership</p>
              <h3 className="font-display text-3xl sm:text-4xl mt-4 uppercase leading-tight">Profit &amp; Cashflow Advisory</h3>
              <p className="mt-5 text-[15px] leading-relaxed text-brand-black">
                Best when the business has outgrown one-off clarity and you want ongoing support
                across profit, cashflow, pay, offers, hiring, investment and the bigger direction
                of the business.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-brand-black flex-grow">
                This is <strong>Part 4 of the method — Own Your Future</strong> — with a strategic
                partner by your side.
              </p>
              <Link to={LINKS.financialLeadership} className="btn-primary shadow-w-default mt-9 self-start" data-testid="comparison-advisory-cta">
                Apply for Advisory <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          <p className="mt-12 text-center text-[16px] leading-relaxed text-brand-ink max-w-3xl mx-auto reveal">
            Many business owners begin with the Strategy Session and move into the Advisory
            Programme later when they realise they don't just want one-off sessions here and there —
            they want someone by their side, walking the territory with them.
          </p>
        </div>
      </section>

      {/* NOT SURE */}
      <section className="bg-white py-20 border-t-[1.5px] border-brand-black/15" data-testid="not-sure-section">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-display text-4xl sm:text-5xl leading-[0.95]">Not sure which fits?</h2>
          <p className="mt-5 text-lg leading-relaxed text-brand-ink">
            Book a free 20-minute chat and we'll figure it out together. No pressure. No pitch. Just
            a calm conversation about where you are in the Follow The Cash™ journey and what
            would actually serve you.
          </p>
          <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" className="btn-yellow mt-8" data-testid="work-final-cta">
            Book your free chat <ArrowUpRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
}
